/**
 * DefaultValueCell — komórka „Wartość domyślna" w liście pól kartoteki.
 *
 * Wartość domyślna to podpowiedź wpisywana w pole, gdy otwiera się formularz NOWEGO rekordu;
 * użytkownik może ją zmienić. Edytor zależy wyłącznie od dziedziny pola z listy pól
 * (`valueDomain` / `notDefaultableReason`, rejestr `core-field-domains` w `@saas/shared`):
 *  - tekst / liczba — edycja w miejscu (`InlineEditableCell`),
 *  - tak/nie, lista zamknięta, opcje wyboru, słownik, kategoria — lista z pozycją „— brak —"
 *    (wybór wielokrotny — `MultiSelect`),
 *  - data — panel (`CellPopover`): stała data albo „dziś ± N dni",
 *  - pole niedostępne — sam powód.
 *
 * Format wartości (tekst w kolumnie `default_value`): tekst; liczba z kropką; `true`/`false`;
 * kod opcji; tablica JSON kodów (wybór wielokrotny); data `YYYY-MM-DD` albo `@today`, `@today+N`,
 * `@today-N`. Brak wartości = `null` (osobno od `false`).
 *
 * Wartość, której nie ma już wśród opcji (np. pozycja słownika albo kategoria usunięta po
 * ustawieniu), zostaje na liście z napisem „— usunięta pozycja” (nie surowym kodem czy id) —
 * wdrożeniowiec widzi, że trzeba wybrać inną albo „— brak —”. Dopóki opcje słownika / kategorii
 * się wczytują, komórka pokazuje wczytywanie, żeby ustawiona wartość nie mignęła jako usunięta.
 */

import { type RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DefaultValueUnavailableReason,
  FieldValueDomain,
  RecordCategoryKartotekaKey,
} from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { CalendarDays } from 'lucide-react';

import { CellPopover } from '@/components/additional/CellPopover';
import { InlineEditableCell } from '@/components/additional/InlineEditableCell';
import { Button } from '@/components/Button';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { MultiSelect } from '@/components/MultiSelect';
import type { RecordCategory } from '@/components/record-categories/RecordCategoryPicker';
import {
  type DictionaryParamBindingsMap,
  formatDictionaryOptionLabel,
  useDictionary,
  useDictionaryOptions,
} from '@/hooks/useDictionary';
import { api } from '@/lib/api';
import { coreFieldOptions } from '@/lib/core-field-option-labels';
import { cn } from '@/lib/utils';

/** Wartość listy oznaczająca „brak wartości domyślnej" (Radix Select nie przyjmuje pustej). */
const NONE = '__none__';

const RELATIVE_DATE = /^@today(?:([+-])(\d+))?$/;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

type DateMode = 'fixed' | 'relative';

interface ParsedDate {
  mode: DateMode;
  fixed: string;
  days: number;
}

interface ChoiceOption {
  value: string;
  label: string;
}

function parseDateDefault(value: string | null): ParsedDate {
  if (value === null) return { mode: 'relative', fixed: '', days: 0 };
  const rel = RELATIVE_DATE.exec(value);
  if (rel) {
    const n = rel[2] === undefined ? 0 : Number(rel[2]);
    return { mode: 'relative', fixed: '', days: rel[1] === '-' ? -n : n };
  }
  return { mode: 'fixed', fixed: value, days: 0 };
}

function serializeRelative(days: number): string {
  if (days === 0) return '@today';
  return days > 0 ? `@today+${days}` : `@today-${Math.abs(days)}`;
}

function parseMultiDefault(value: string | null): string[] {
  if (value === null) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/** Dokłada ustawione wartości spoza opcji (z napisem „usunięta pozycja”), żeby nie znikały z listy. */
function withSelected(
  options: readonly ChoiceOption[],
  selected: readonly string[],
  removedLabel: string,
): ChoiceOption[] {
  const known = new Set(options.map((o) => o.value));
  return [
    ...options,
    ...selected.filter((v) => !known.has(v)).map((v) => ({ value: v, label: removedLabel })),
  ];
}

export interface DefaultValueCellField {
  slug: string;
  fieldType: string;
  defaultable: boolean;
  valueDomain: FieldValueDomain | null;
  notDefaultableReason: DefaultValueUnavailableReason | null;
}

export interface DefaultValueCellProps {
  moduleSlug: string;
  field: DefaultValueCellField;
  value: string | null;
  onChange: (next: string | null) => void;
}

export function DefaultValueCell({ moduleSlug, field, value, onChange }: DefaultValueCellProps) {
  const { t } = useTranslation();
  const testId = `system-module-field-${field.slug}-default`;
  const noneLabel = t('tenant.systemModuleFields.defaultValue.none');
  const domain = field.valueDomain;

  if (!field.defaultable || domain === null) {
    return (
      <span className="text-xs text-text-muted" data-testid={testId}>
        {unavailableText(field.notDefaultableReason, field.fieldType, t)}
      </span>
    );
  }

  const choice = (options: readonly ChoiceOption[], multiple: boolean) => (
    <ChoiceDefaultEditor
      options={options}
      multiple={multiple}
      value={value}
      onChange={onChange}
      data-testid={testId}
    />
  );

  switch (domain.kind) {
    case 'text':
    case 'number':
      return (
        <InlineEditableCell<string | null>
          value={value}
          onCommit={(next) => onChange(next === null || next.trim() === '' ? null : next.trim())}
          renderDisplay={(v) => (
            <span className={cn('text-sm', v === null && 'text-text-muted')}>{v ?? noneLabel}</span>
          )}
          renderInput={(draft, setDraft, onKeyDown, onBlur, ref) => (
            <Input
              ref={ref as RefObject<HTMLInputElement>}
              size="sm"
              type={domain.kind === 'number' ? 'number' : 'text'}
              value={draft ?? ''}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              aria-label={t('tenant.systemModuleFields.defaultValue.label')}
            />
          )}
          data-testid={testId}
        />
      );

    case 'boolean':
      return choice(
        [
          { value: 'true', label: t('tenant.systemModuleFields.defaultValue.yes') },
          { value: 'false', label: t('tenant.systemModuleFields.defaultValue.no') },
        ],
        false,
      );

    case 'codes':
      return choice(coreFieldOptions(moduleSlug, field.slug, domain.codes, t), domain.multiple);

    case 'options':
      return choice(domain.options, domain.multiple);

    case 'dictionary':
      return (
        <DictionaryDefaultEditor
          dictionaryCode={domain.dictionaryCode}
          paramBindings={domain.paramBindings}
          multiple={domain.multiple}
          value={value}
          onChange={onChange}
          data-testid={testId}
        />
      );

    case 'category':
      return (
        <CategoryDefaultEditor
          kartotekaKey={domain.kartotekaKey}
          value={value}
          onChange={onChange}
          data-testid={testId}
        />
      );

    case 'date':
      return (
        <DateDefaultEditor
          value={value}
          withTime={domain.withTime}
          onChange={onChange}
          data-testid={testId}
        />
      );
  }
}

function unavailableText(
  reason: DefaultValueUnavailableReason | null,
  fieldType: string,
  t: TFunction,
): string {
  switch (reason) {
    case 'auto':
      return t('tenant.systemModuleFields.defaultValue.unavailable.auto');
    case 'not_saved_on_create':
      return t('tenant.systemModuleFields.defaultValue.unavailable.notSavedOnCreate');
    case 'record_dependent':
      return t('tenant.systemModuleFields.defaultValue.unavailable.recordDependent');
    case 'module_unsupported':
    case null:
      return t('tenant.systemModuleFields.defaultValue.unavailable.moduleUnsupported');
    case 'unsupported_type':
      return fieldType === 'file'
        ? t('tenant.systemModuleFields.defaultValue.unavailable.file')
        : t('tenant.systemModuleFields.defaultValue.unavailable.reference');
  }
}

// ─── Wybór: lista zamknięta, opcje wyboru, słownik, kategoria, tak/nie ─────────

interface ChoiceDefaultEditorProps {
  options: readonly ChoiceOption[];
  multiple: boolean;
  value: string | null;
  onChange: (next: string | null) => void;
  'data-testid': string;
}

function ChoiceDefaultEditor({
  options,
  multiple,
  value,
  onChange,
  'data-testid': testId,
}: ChoiceDefaultEditorProps) {
  const { t } = useTranslation();
  const noneLabel = t('tenant.systemModuleFields.defaultValue.none');
  const removedLabel = t('tenant.systemModuleFields.defaultValue.removedOption');

  if (multiple) {
    const selected = parseMultiDefault(value);
    return (
      <MultiSelect
        value={selected}
        onChange={(next) => onChange(next.length === 0 ? null : JSON.stringify(next))}
        options={withSelected(options, selected, removedLabel)}
        placeholder={noneLabel}
        data-testid={testId}
      />
    );
  }

  return (
    <Dropdown
      value={value ?? NONE}
      onChange={(next) => onChange(next === NONE ? null : next)}
      options={[
        { value: NONE, label: noneLabel },
        ...withSelected(options, value ? [value] : [], removedLabel),
      ]}
      aria-label={t('tenant.systemModuleFields.defaultValue.label')}
      data-testid={testId}
    />
  );
}

interface SourcedEditorProps {
  value: string | null;
  onChange: (next: string | null) => void;
  'data-testid': string;
}

/** Komunikat zamiast edytora (powód braku wartości albo błąd wczytania opcji). */
function CellNote({ text, 'data-testid': testId }: { text: string; 'data-testid': string }) {
  return (
    <span className="text-xs text-text-muted" data-testid={testId}>
      {text}
    </span>
  );
}

/**
 * Pozycje słownika tenanta (aktywne), etykieta wg trybu wyświetlania słownika. Słownik ze stałymi
 * parametrami — opcje pobierane z tymi parametrami (jak na formularzu rekordu). Słownik, którego
 * źródło samo wymaga kontekstu (np. firmy), nie ma jednej listy — pokazujemy powód, nie pustą listę.
 */
function DictionaryDefaultEditor({
  dictionaryCode,
  paramBindings,
  multiple,
  ...rest
}: SourcedEditorProps & {
  dictionaryCode: string;
  paramBindings: Readonly<Record<string, unknown>> | null;
  multiple: boolean;
}) {
  const { t } = useTranslation();
  // Oba hooki bezwarunkowo — aktywny jest jeden (pusty kod / `enabled: false` wyłącza drugi).
  const withParams = paramBindings !== null;
  const plain = useDictionary(withParams ? '' : dictionaryCode);
  const bound = useDictionaryOptions(dictionaryCode, {
    bindings: paramBindings as DictionaryParamBindingsMap | null,
    recordValues: {},
    enabled: withParams,
  });
  const { dictionary, activeItems, isError, isLoading } = withParams ? bound : plain;

  if (isLoading) {
    return (
      <CellNote
        text={t('tenant.systemModuleFields.defaultValue.loading')}
        data-testid={rest['data-testid']}
      />
    );
  }
  if (isError) {
    return (
      <CellNote
        text={t('tenant.systemModuleFields.defaultValue.loadError')}
        data-testid={rest['data-testid']}
      />
    );
  }
  if (dictionary?.itemsUnavailableReason === 'PARAMS_REQUIRED') {
    return (
      <CellNote
        text={t('tenant.systemModuleFields.defaultValue.unavailable.recordDependent')}
        data-testid={rest['data-testid']}
      />
    );
  }
  const options = dictionary
    ? activeItems.map((item) => ({
        value: item.code,
        label: formatDictionaryOptionLabel(item, dictionary.displayMode),
      }))
    : [];
  return <ChoiceDefaultEditor options={options} multiple={multiple} {...rest} />;
}

/** Kategorie kartoteki (`record_categories`) — wartość = id kategorii. */
function CategoryDefaultEditor({
  kartotekaKey,
  ...rest
}: SourcedEditorProps & { kartotekaKey: RecordCategoryKartotekaKey }) {
  const { t } = useTranslation();
  // Ten sam klucz zapytania co pole „Kategoria" formularza (`RecordCategoryPicker`) — wspólny cache.
  const categoriesQ = useQuery({
    queryKey: ['record-categories', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ categories: RecordCategory[] }>('/record-categories', {
        params: { kartoteka: kartotekaKey },
      });
      return res.data.categories;
    },
    staleTime: 60_000,
  });
  if (categoriesQ.isPending) {
    return (
      <CellNote
        text={t('tenant.systemModuleFields.defaultValue.loading')}
        data-testid={rest['data-testid']}
      />
    );
  }
  if (categoriesQ.isError) {
    return (
      <CellNote
        text={t('tenant.systemModuleFields.defaultValue.loadError')}
        data-testid={rest['data-testid']}
      />
    );
  }
  const options = (categoriesQ.data ?? []).map((c) => ({ value: c.id, label: c.name }));
  return <ChoiceDefaultEditor options={options} multiple={false} {...rest} />;
}

// ─── Data: stała albo „dziś ± N dni" ──────────────────────────────────────────

interface DateDefaultEditorProps {
  value: string | null;
  withTime: boolean;
  onChange: (next: string | null) => void;
  'data-testid': string;
}

function DateDefaultEditor({
  value,
  withTime,
  onChange,
  'data-testid': testId,
}: DateDefaultEditorProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const initial = parseDateDefault(value);
  const [mode, setMode] = useState<DateMode>(initial.mode);
  const [fixed, setFixed] = useState(initial.fixed);
  const [days, setDays] = useState(String(initial.days));

  const openEditor = () => {
    const parsed = parseDateDefault(value);
    setMode(parsed.mode);
    setFixed(parsed.fixed);
    setDays(String(parsed.days));
    setOpen(true);
  };

  const daysNumber = Number.parseInt(days, 10);
  const canSave = mode === 'fixed' ? ISO_DATE.test(fixed) : Number.isFinite(daysNumber);

  const save = () => {
    if (!canSave) return;
    onChange(mode === 'fixed' ? fixed : serializeRelative(daysNumber));
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setOpen(false);
  };

  return (
    <>
      <span ref={anchorRef} className="inline-flex">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<CalendarDays className="h-4 w-4" />}
          onClick={openEditor}
          className={cn(value === null && 'text-text-muted')}
          data-testid={testId}
        >
          {formatDateDefault(value, t)}
        </Button>
      </span>
      <CellPopover
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        ariaLabel={t('tenant.systemModuleFields.defaultValue.label')}
        className="w-72 space-y-3 p-3"
        data-testid={`${testId}-popover`}
      >
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === 'relative' ? 'tertiary' : 'secondary'}
            onClick={() => setMode('relative')}
            data-testid={`${testId}-mode-relative`}
          >
            {t('tenant.systemModuleFields.defaultValue.date.relative')}
          </Button>
          <Button
            size="sm"
            variant={mode === 'fixed' ? 'tertiary' : 'secondary'}
            onClick={() => setMode('fixed')}
            data-testid={`${testId}-mode-fixed`}
          >
            {t('tenant.systemModuleFields.defaultValue.date.fixed')}
          </Button>
        </div>
        {mode === 'fixed' ? (
          <DatePicker value={fixed} onChange={setFixed} data-testid={`${testId}-fixed`} />
        ) : (
          <Input
            size="sm"
            type="number"
            label={t('tenant.systemModuleFields.defaultValue.date.days')}
            hint={
              withTime ? t('tenant.systemModuleFields.defaultValue.date.withTimeHint') : undefined
            }
            value={days}
            onChange={(e) => setDays(e.target.value)}
            data-testid={`${testId}-days`}
          />
        )}
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={clear} data-testid={`${testId}-clear`}>
            {t('tenant.systemModuleFields.defaultValue.date.clear')}
          </Button>
          <Button size="sm" onClick={save} disabled={!canSave} data-testid={`${testId}-save`}>
            {t('tenant.systemModuleFields.defaultValue.date.save')}
          </Button>
        </div>
      </CellPopover>
    </>
  );
}

function formatDateDefault(value: string | null, t: TFunction): string {
  if (value === null) return t('tenant.systemModuleFields.defaultValue.none');
  const rel = RELATIVE_DATE.exec(value);
  if (rel) {
    const n = rel[2] === undefined ? 0 : Number(rel[2]);
    if (n === 0) return t('tenant.systemModuleFields.defaultValue.date.today');
    if (rel[1] === '+') {
      return n === 1
        ? t('tenant.systemModuleFields.defaultValue.date.plusOne')
        : t('tenant.systemModuleFields.defaultValue.date.plus', { count: n });
    }
    return n === 1
      ? t('tenant.systemModuleFields.defaultValue.date.minusOne')
      : t('tenant.systemModuleFields.defaultValue.date.minus', { count: n });
  }
  const iso = ISO_DATE.exec(value);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : value;
}
